import { supabase } from '@/shared/supabase/client'
import { isScreenshotFixtureMode } from '@/shared/lib/screenshotMode'
import { screenshotFixtureEvents } from '@/shared/screenshotSeed/synapScreenshotFixtures'
import { AppError } from '@/shared/lib/appError'
import { toIsoUtcForStorage } from '@/shared/lib/date'
import { toRepositoryError } from '@/shared/repositories/common/repositoryError'
import type { Database } from '@/shared/supabase/database.types'

export type BookingStatus = 'confirmed' | 'pending' | 'cancelled' | null

export interface ApiEvent {
  id: string
  title: string
  description?: string
  location?: string
  locationId?: string | null
  startDateTime: string
  endDateTime: string
  allDay: boolean
  patientId?: string
  reminderEnabled?: boolean
  reminderMinutesBefore?: number
  bookingStatus?: BookingStatus
  createdAt: string
  updatedAt: string
}

export type CreateEventPayload = Omit<ApiEvent, 'id' | 'createdAt' | 'updatedAt'>

type EventRow = Database['public']['Tables']['events']['Row']

const getCurrentUserId = async (): Promise<string> => {
  const { data, error } = await supabase.auth.getUser()
  if (error) {
    throw toRepositoryError(error, 'Não foi possível validar a sessão do usuário.', 401)
  }
  if (!data.user) {
    throw new AppError('Usuário não autenticado.', { status: 401, code: 'UNAUTHORIZED' })
  }
  return data.user.id
}

const toApiEvent = (row: EventRow): ApiEvent => ({
  id: row.id,
  title: row.title,
  description: row.description ?? undefined,
  location: row.location ?? undefined,
  locationId: row.location_id ?? undefined,
  startDateTime: row.start_dt,
  endDateTime: row.end_dt,
  allDay: Boolean(row.all_day),
  patientId: row.patient_id ?? undefined,
  reminderEnabled: row.reminder_enabled ?? undefined,
  reminderMinutesBefore: row.reminder_minutes_before ?? undefined,
  bookingStatus: row.booking_status ?? undefined,
  createdAt: row.created_at,
  updatedAt: row.updated_at,
})

export const eventsRepository = {
  list: async (): Promise<{ events: ApiEvent[] }> => {
    if (isScreenshotFixtureMode()) {
      return { events: screenshotFixtureEvents() }
    }
    try {
      await getCurrentUserId()
      const { data, error } = await supabase
        .from('events')
        .select('*')
        .order('start_dt', { ascending: true })

      if (error) {
        throw toRepositoryError(error, 'Não foi possível listar eventos.')
      }

      return { events: (data ?? []).map(toApiEvent) }
    } catch (error) {
      throw toRepositoryError(error, 'Não foi possível listar eventos.')
    }
  },

  create: async (payload: CreateEventPayload): Promise<{ event: ApiEvent }> => {
    try {
      const ownerUserId = await getCurrentUserId()
      const { data, error } = await supabase
        .from('events')
        .insert({
          owner_user_id: ownerUserId,
          title: payload.title.trim(),
          description: payload.description?.trim() || null,
          location: payload.location?.trim() || null,
          location_id: payload.locationId ?? null,
          start_dt: toIsoUtcForStorage(payload.startDateTime),
          end_dt: toIsoUtcForStorage(payload.endDateTime),
          all_day: payload.allDay,
          patient_id: payload.patientId ?? null,
          reminder_enabled: payload.reminderEnabled ?? null,
          reminder_minutes_before: payload.reminderMinutesBefore ?? null,
        })
        .select('*')
        .single()

      if (error) {
        throw toRepositoryError(error, 'Não foi possível criar evento.')
      }

      return { event: toApiEvent(data) }
    } catch (error) {
      throw toRepositoryError(error, 'Não foi possível criar evento.')
    }
  },

  update: async (
    eventId: string,
    payload: Partial<CreateEventPayload>,
  ): Promise<{ event: ApiEvent }> => {
    try {
      await getCurrentUserId()
      const updates: Record<string, unknown> = {}
      if (payload.title !== undefined) updates.title = payload.title.trim()
      if (payload.description !== undefined) updates.description = payload.description?.trim() || null
      if (payload.location !== undefined) updates.location = payload.location?.trim() || null
      if (payload.locationId !== undefined) updates.location_id = payload.locationId ?? null
      if (payload.startDateTime !== undefined) updates.start_dt = toIsoUtcForStorage(payload.startDateTime)
      if (payload.endDateTime !== undefined) updates.end_dt = toIsoUtcForStorage(payload.endDateTime)
      if (payload.allDay !== undefined) updates.all_day = payload.allDay
      if (payload.patientId !== undefined) updates.patient_id = payload.patientId ?? null
      if (payload.reminderEnabled !== undefined) updates.reminder_enabled = payload.reminderEnabled ?? null
      if (payload.reminderMinutesBefore !== undefined)
        updates.reminder_minutes_before = payload.reminderMinutesBefore ?? null
      if (payload.bookingStatus !== undefined)
        updates.booking_status = payload.bookingStatus ?? null
      if (Object.keys(updates).length === 0) {
        const { data } = await supabase.from('events').select('*').eq('id', eventId).single()
        if (!data) throw new AppError('Evento não encontrado.', { status: 404 })
        return { event: toApiEvent(data) }
      }
      const { data, error } = await supabase
        .from('events')
        .update(updates as never)
        .eq('id', eventId)
        .select('*')
        .single()
      if (error) {
        throw toRepositoryError(error, 'Não foi possível atualizar o evento.')
      }
      return { event: toApiEvent(data) }
    } catch (error) {
      throw toRepositoryError(error, 'Não foi possível atualizar o evento.')
    }
  },

  delete: async (eventId: string): Promise<void> => {
    try {
      await getCurrentUserId()
      const { error } = await supabase.from('events').delete().eq('id', eventId)

      if (error) {
        throw toRepositoryError(error, 'Não foi possível excluir o evento.')
      }
    } catch (error) {
      throw toRepositoryError(error, 'Não foi possível excluir o evento.')
    }
  },
}
